/**
 * @fileoverview Today: what to do in the garden today, and what today is.
 *
 * The app opens here, and so does the welcome, one step at a time:
 *
 * 1. Where the garden is. A garden without a postcode is asked "Where is
 *    your garden, Sam?" and for its ZIP code in the place of To do
 *    (TodayPostcode); the other tabs and the trial line stay hidden until it
 *    is given. A founding gardener's welcome line stands above it.
 * 2. Their place, at once: the place as the heading ("Sparta, TN"), the
 *    brief's frost countdown and this week in two lines, then "Your garden
 *    starts here" and two ways to start (WelcomePlace, B96): a photo of
 *    anything (seed packets, a seed order, plants already growing, all read
 *    by the one photo path, B80), or typing a plant. For a garden with no plants, and a new account until it
 *    adds a plant of its own (the first-step tip, useFirstStep). The almanac
 *    and the weather wait.
 * 3. Then Today, below. Every gardener always finds "Something
 *    confusing or broken? Let us know" at its end, which opens Report a problem.
 *
 * A garden that has never had a plant skips all of that (B30, design revision 2): at
 * once, before the brief, the outlook or the weather are in, it shows only
 * "Your garden starts here" and the two doors, a photo and a typed (B96; lib/todayStart decides, from
 * the server's `started` in the garden place, B64; WelcomePlace draws them), with no frost or weather
 * line and no "No plants yet" line. Photos waiting to be sorted are the one exception (B78): the server names the "Sort your
 * photos" job (brief.todo.startJob) and it is drawn above "Your garden starts here", as Today draws it. A garden whose plants are all archived has
 * started (B64): it is Today as for any garden, with the server's one-line note
 * (brief.note) as the weather card's last line, linked to the path the note carries (the archive; or, B137, the Seed vault for a
 * garden of kept seeds). A gardener with plants sees no change, and
 * never the doors: until the garden place answers (it carries `started`) Today
 * keeps its normal loader (C97: the per-device plants hint is gone). The photo picker
 * and its "Check and save" sheet (the photo door) and the add form (the type door) are owned by TodayPage itself, beside the views,
 * so they survive the first plant turning the doors into Today.
 *
 * Which step shows is decided only once the advice is in (it comes with the
 * brief; or TODAY_SETTLE_MAX_MS has passed): until then a quiet loader, so the
 * ZIP step does not flash Today itself before the place step. Each step
 * fades in (keyed by the step).
 *
 * What Today says is the backend's (the owner's rule: the backend is for
 * business logic, the front end is display; the to-do rebuild, batch 5: the
 * app only displays). The Today brief (GET /today/brief, shared/api/todayBrief;
 * services/today/todayBrief) decides everything and sends it in the
 * gardener's language, for the garden's own day (brief.date, never the
 * phone's): the heading, the weather strip's line, the frost countdown
 * (owner-set wording) and the Almanac's lines, "Water today?" and where it
 * goes (water.placement: always 'top', first under the Almanac; water.jobKeys), the quiet line, each coming day's sky, high and
 * line (comingUp.days), and the one to-do list (todo), with the advice the
 * server placed for this person (todo.reads: the notice, the reminder under
 * the weather, the welcome's steps; through useTips). This page lays it out
 * and keeps only what is display: the month's opened days for the rain month
 * (useRainMonth), which job's plants are open.
 *
 * Top to bottom:
 *
 * 1. The season's painting, or the garden's own photo, as a thin strip
 *    (TodayHeader); before a postcode, the painting for the time of year.
 *    At the very top, above the picture, an urgent notice when the server
 *    placed one. Grow's own frost or freeze warning is the slim bar under the header
 *    on every page (AlertBar; owner, 2026-10-16), not a card here.
 * 2. The date as the page's heading, under the brief's Almanac heading
 *    ("FALL ALMANAC · RALEIGH, NC / Sunday, October 25"; B91, option B,
 *    owner 2026-10-26, which undoes the part of D46 that moved the Almanac into
 *    the weather card), and under the date, muted, the Almanac (TodayAlmanac):
 *    the moon and season lines every day, then the dates that fall today, the
 *    wettest or driest month on the first days the page is opened in it, and
 *    the panel's Almanac rules. The heading is the brief's one heading; during
 *    the welcome there are no Almanac lines.
 * 3. The weather card (owner, 2026-10-09: "a weather-aware to do"): the
 *    weather now on top, the reading and the sky, then the brief's strip line
 *    (tonight's low, the rain behind and ahead), opening the Weather page;
 *    under it the frost countdown, leading to that season's dates on Weather.
 *    While the list is on its way, the card ends with the loader's last line;
 *    a garden with no plants says so; a brief or forecast that never arrived says why.
 * 4. The one to-do list (D51; owner, 2026-10-15): From yesterday, Today,
 *    Coming up and When you can as job cards (TodoGroups, JobCard), the same
 *    cards as the top of Plants, ticked through POST /todo/marks
 *    (useTodoMarks); under "To do" (the Today group: a visible heading, B60)
 *    "Add a to-do" is its first card, and a card at its end holds the quiet
 *    day's answer and the postponed lines; "Water today?" is the page's first
 *    section, under the Almanac and above the weather card (WaterSection), with its question as the
 *    heading, then the day's water job card(s) the server names (water.jobKeys) or, with none, its one
 *    line (water.placement 'top'; B139, B152); the one reminder between Today and
 *    Coming up; in Coming up, each day its jobs are for opens with that day's
 *    weather (opened by the day's name, E14) drawn as today's is (owner, 2026-10-16: "style it like today
 *    is"): its sky (the server's words and icon key), its high, and the brief's line for it ("Low 37°
 *    Saturday night · Rain 0.4″"). /today?job= (the emails' links) opens that
 *    job. The gardener's own to-dos (D50) are cards on the same list, with
 *    Edit in their foot; "Add a to-do" (D68, B60) opens the own
 *    to-do sheet (features/own-todos useOwnTodoSheet: POST /todo/own, PATCH
 *    /todo/own/:id), and "Plant something" beside it (E25) the sheet that adds
 *    a plant at Planning and its to-do together (POST /todo/own/plant); the
 *    new card is shown and scrolled to.
 * 5. This week, from your planner: the planner's general notes for the
 *    week, whole, as written, to read rather than to tick.
 *
 * Where: the route /today (App.tsx; / goes there too), the page the app
 * opens on, inside StagesProvider and PlantsProvider.
 * API: GET /today/brief?lang=&unit=&days= (useTodayBrief, the one read the
 * Plants, Beds and Weather pages share); GET /header (the weather card's
 * reading, sky words and icon key, and the "no forecast yet" sentence, through
 * useWeatherNow and useWeatherContext: the header's own answer; W24 Job D); GET
 * /today?zip= (getToday: season, painting or photo, planner notes, for the
 * garden's own day); GET /garden-place (useGardenPostcode: whether the
 * garden can be planned, its postcode, its town for the welcome and its day,
 * all the server's: the owner's in a shared garden; audit A29 and A31);
 * POST /todo/marks (advice seen or put away, through useTips). Today waits
 * on the brief and the garden place only (W24 Job C): it reads neither the
 * plant list nor the plant outlook (the brief's hasOutlook says whether
 * there is one), so a mark on a to-do reads the brief again and nothing else
 * here.
 * Settings: the garden's postal code as the server has it (none, or one Grow
 * cannot use, is the ZIP step; for a member of a shared garden, a line saying
 * the owner gives it), temperature unit, language and Coming up days (useTodayBrief);
 * TODAY_SETTLE_MAX_MS (config/tunables/today).
 * Words (en/es): today.firstStep, today.almanac, today.thisWeek,
 * today.fromPlanner, welcome.zip.question(NoName),
 * welcome.zip.ownersGarden (useGardenPostcode),
 * today.report(Prompt), weather.* (title, noPlants, error),
 * loader.today.*. Everything else on the page is the brief's words.
 * Watch out: which step shows (ZIP, place, Today) waits for the garden place
 * and the advice (or TODAY_SETTLE_MAX_MS) so the ZIP step never flashes Today
 * first; the step is the outer div's key, so a new step remounts and fades
 * in. Failures on our side (a 500, no connection; D31, lib/serverFailure):
 * a brief or forecast that never arrived says the standard sentence in the
 * card instead of weather.error, reported to Sentry; a garden the weather service cannot place yet shows the
 * server's own sentence (noForecastMessage, B44) where the weather would be; a mark that did not
 * save says it under its job (useTodoMarks).
 *
 * @module TodayPage
 */

import { retryRule } from '@/config/queryRetry';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Snowflake } from 'lucide-react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useAuth } from '@/shared/hooks/useAuth';
import { useGardenPostcode } from '@/shared/hooks/useGardenPostcode';
import { useWeatherNow } from '@/shared/hooks/useWeatherNow';
import { useWeatherContext } from '@/shared/hooks/useWeatherContext';
import { DateTime } from '@/shared/utils/dateUtils';
import { QUERY_STALE_TIME_MS } from '@/config/queryClient';
import { TODAY_QUERY_KEY, TODAY_QUERY_RETRIES, TODAY_SETTLE_MAX_MS, TODAY_FIRST_PLANT_HOLD_MAX_MS } from '@/config/tunables/today';
import { GrowLoader } from '@/shared/components/ui/GrowLoader';
import { GuidanceQuiet, GuidanceUpdating } from '@/shared/components/GuidanceUpdating';
import { ServerFailureNote } from '@/shared/components/ServerFailure';
import { isServerFailure } from '@/shared/lib/serverFailure';
import { getToday } from '@/shared/api/today';
import { useTodayBrief } from '@/shared/hooks/useTodayBrief';
import { TopTip } from '@/features/tips/TipViews';
import { useFirstStep, usePostcodeTip } from '@/features/tips/useFirstStep';
import { useTips } from '@/features/tips/useTips';
import { WelcomePlace } from '@/features/welcome/WelcomePlace';
import { canReportProblem } from '@/shared/lib/sentry';
import { openReportProblem } from '@/shared/lib/reportProblem';
import { TodayHeader } from './components/TodayHeader';
import { TodayPostcode } from './components/TodayPostcode';
import { TodayNotice } from './components/TodayNotice';
import { TodayAlmanac } from './components/TodayAlmanac';
import { useRainMonth } from './lib/useRainMonth';
import { showDoorsOnly, firstStepDecision } from './lib/todayStart';
import { waitingForTips, type TipsGate } from './lib/tipsGate';
import { useDoorPhotos } from './lib/useDoorPhotos';
import { useDoorAddForm } from './lib/useDoorAddForm';
import { WaterSection } from './components/WaterSection';
import { TodoGroups } from './components/TodoGroups';
import { JobCard } from './components/JobCard';
import { useTodoMarks } from './lib/useTodoMarks';
import { useOwnTodoSheet } from '@/features/own-todos/useOwnTodoSheet';
import { SkyIcon } from '@/shared/components/SkyIcon';
import { TOKEN_TINT } from '@/shared/theme/verdictTokens';

/** A panel on the page, in each theme's card colours, as the Plants page's weather bar is. */
const PANEL = 'rounded-lg border border-border bg-card p-3';
/**
 * The weather card: the panel's colours with no padding of its own, so the
 * weather strip and the frost line run edge to edge.
 */
const CARD = 'overflow-hidden rounded-lg border border-border bg-card';
/**
 * The card's two columns, shared by every line in it so they read on one
 * edge (owner, 2026-10-09: "the indent is odd"): an icon slot the width of a
 * task's tick box (CARD_SLOT, 2.75rem), then the text. With the row's
 * padding and gap the text starts 3.5rem in (CARD_TEXT_EDGE).
 */
const CARD_ROW = 'flex gap-1.5 px-1.5';
/** The card's icon slot: a tick box wide, its icon centred. */
const CARD_SLOT = 'flex w-11 shrink-0 justify-center';
/** A card line with no icon: text starting at the shared edge. */
const CARD_TEXT_EDGE = 'pl-14 pr-3.5';
/** A panel's small heading. */
const PANEL_TITLE = 'mb-2 flex items-baseline gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground';
/** A line's words as a link, with the same light underline as the plant names in To do (owner, 2026-10-09: the heavy one read rough). */
const LINE_LINK = 'rounded-sm underline decoration-[color-mix(in_srgb,currentColor_40%,transparent)] decoration-1 underline-offset-[5px] hover:decoration-current focus:outline-none focus-visible:decoration-current focus-visible:ring-2 focus-visible:ring-ring';

/**
 * The page's views: the start doors alone, the welcome's steps, or the day with its jobs.
 *
 * @param props.onPhotos - Opens the photo picker (the photo door, B80), which TodayPage owns so it outlives a swap of views.
 * @param props.onAdd - Opens the add-a-plant form (the type door), owned by TodayPage for the same reason.
 */
const TodayView: React.FC<{ onPhotos: () => void; onAdd: () => void }> = ({ onPhotos, onAdd }) => {
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  const { mysqlUser } = useAuth();
  /*
   * Where the garden is and its day, as the server says them (the owner's postcode and day in a shared garden):
   * whether Today asks for a postcode, and the day the painting and planner notes are for.
   */
  const gardenPostcode = useGardenPostcode();
  const { zip, date: gardenDate } = gardenPostcode;

  /*
   * The brief: everything Today decides, from the backend, in the gardener's
   * language and unit, for the garden's own day (useTodayBrief, the one read
   * the Plants, Beds and Weather pages share), and whether there is a
   * postcode Grow can plan from, as the server says: one it cannot (saved
   * before postcodes were checked) is asked again, with why (useGardenPostcode).
   */
  const { brief, isLoading: briefLoading, isError: briefFailed, error: briefError, noZip, queryKey: briefKey, ask, rereading: briefRereading } = useTodayBrief();
  /** The garden's day: the brief's, else the garden place's until it is in (never the phone's). */
  const today = brief?.date ?? gardenDate;

  const { data: day, isError: dayFailed } = useQuery({
    queryKey: [TODAY_QUERY_KEY, zip, gardenDate],
    queryFn: () => getToday(zip),
    // Without a postcode too: the painting for the time of year, behind the welcome.
    enabled: gardenPostcode.known && gardenDate.length > 0,
    staleTime: QUERY_STALE_TIME_MS,
    retry: retryRule(TODAY_QUERY_RETRIES),
  });
  const weatherNow = useWeatherNow();
  /** The server's sentence for a garden the weather service cannot place yet (B44), else null. */
  const { noForecastMessage } = useWeatherContext();
  const rainShown = useRainMonth(today);

  /** Ticking the one to-do list (D51): the server does it and answers with the brief afresh. */
  const todoMarks = useTodoMarks(briefKey, ask);
  /** The gardener's own to-dos' sheet (D50): Add a to-do, and a card's Edit. */
  const ownSheet = useOwnTodoSheet(briefKey, ask, todoMarks, today);
  /** The job an email linked to (/today?job=), opened and scrolled to. */
  const [searchParams] = useSearchParams();
  const linkedJob = searchParams.get('job');
  // B64: whether the garden has started, the server's word alone (C118 F1): the app's plants list never overrides it. A saved plant reads the garden place again (PlantsContext refreshGarden).
  const started = gardenPostcode.started;
  // The first step waits for the postcode (a plant added for nowhere has no week to show); the advice comes with the brief.
  const { tip: placedTip } = useFirstStep(!noZip);
  // The postcode step's words, from its tip (a rule on the Grow settings panel's Today tab, Tips).
  const { words: postcodeWords } = usePostcodeTip(noZip);
  const name = mysqlUser?.display_name?.trim().split(' ')[0] ?? '';
  const tips = useTips();
  // The place, for the welcome's heading ("Sparta, TN"): the garden place's town and state, else its ZIP.
  const placeName = [gardenPostcode.city, gardenPostcode.state].filter(Boolean).join(', ') || zip || null;
  /*
   * Whether the step can be chosen yet: the advice is in (whose first step is
   * the place step; it comes with the brief), or it has been waited for long
   * enough. Before a ZIP there is only the ZIP step.
   */
  const [settleTimedOut, setSettleTimedOut] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setSettleTimedOut(true), TODAY_SETTLE_MAX_MS);
    return () => window.clearTimeout(timer);
  }, []);
  // The first step is the brief's, with the brief's own `started` (C118 F4). After a plant is saved the held brief is older than the garden: Today's loader holds while the brief is read again (or the hold's own limit, which starts when the hold does).
  const staleRead = Boolean(placedTip) && briefRereading;
  const [holdTimedOut, setHoldTimedOut] = useState(false);
  useEffect(() => {
    if (!staleRead) return undefined;
    const timer = window.setTimeout(() => setHoldTimedOut(true), TODAY_FIRST_PLANT_HOLD_MAX_MS);
    return () => window.clearTimeout(timer);
  }, [staleRead]);
  const firstStep = firstStepDecision({ tipPlaced: Boolean(placedTip), started: brief?.started ?? false, rereading: briefRereading, timedOut: holdTimedOut });
  const firstTip = firstStep.useTip ? placedTip : null;
  // The garden place first: until the server says where the garden is, neither the ZIP step nor Today.
  // The wait for the tips ends the first time they are ready: a re-read of a failed brief must not bring the loader back (B109).
  const tipsGate = useRef<TipsGate>({ readyOnce: false });
  const waitingTips = waitingForTips(tipsGate.current, tips.ready, settleTimedOut);
  const settling = (!gardenPostcode.known && !gardenPostcode.isError)
    || (!noZip && (firstStep.hold || waitingTips));
  /** The welcome step showing, for its fade: each new step fades in. */
  const step = noZip ? 'zip' : firstTip ? 'place' : 'today';
  /** The fade a step comes in with; none for those who ask for less motion. */
  const fadeIn = 'motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300';

  /** The heading while Today welcomes somebody, else null for the date. */
  const welcome = noZip
    ? (postcodeWords?.title || (name ? t('welcome.zip.question', { name }) : t('welcome.zip.questionNoName')))
    : firstTip ? placeName : null;

  /*
   * The notice the server placed (todo.reads.notice): an urgent one at the very top, a quiet one under the weather in
   * the place of the reminder there (the server places only one of the two there). Put away through useTips.
   */
  const placedNotice = tips.read('notice');
  const urgentNotice = placedNotice?.urgent ? placedNotice : null;
  const notice = placedNotice && !placedNotice.urgent ? placedNotice : null;

  /** The date under the heading: the garden's day, in the reader's language. */
  const dateLine = today ? DateTime.fromISO(today).setLocale(language).toLocaleString({ weekday: 'long', month: 'long', day: 'numeric' }) : '';
  /** The heading over the date, the brief's one heading: the Almanac's, "Fall almanac · Raleigh, NC" (B91); none until the brief is in. */
  const heading = brief?.heading ?? null;

  // The weather now: the reading, the sky's words and its icon key, the server's (useWeatherNow), so Today and the header agree.
  const temperature = weatherNow?.display ?? null;
  const words = weatherNow?.words || null;
  /** The weather strip's second line: the brief's (tonight's low, the rain behind and ahead). */
  const stripLine = brief?.strip.text ?? '';

  /** The lines the first load waits with (GrowLoader). */
  const todaySteps = [t('loader.today.garden'), t('loader.today.weather'), t('loader.today.jobs')];
  // A new garden (B64): it never had a plant of its own. A garden with every plant archived is started, so it is not this.
  const noPlants = gardenPostcode.known && started === false;
  const waiting = !noZip && briefLoading;
  // A forecast or a brief that never arrived is not a quiet day: saying "nothing needs you" would be a guess. The brief says whether the outlook is there (hasOutlook).
  const noForecast = briefFailed || (brief !== undefined && !brief.hasOutlook);
  /** The one to-do list, once it can be drawn: never a list of the app's own, so nothing else flashes first. */
  const todo = !waiting && !noForecast ? brief?.todo ?? null : null;

  /* "Water today?" and Grow's answer, where the server places it: always 'top', first under the Almanac, above the weather card (B152). */
  const water = brief?.water ?? null;
  const answer = water?.answer ?? null;
  const waterAtTop = Boolean(todo && water && answer && water.placement === 'top');

  const countdown = brief?.countdown ?? null;

  /**
   * A coming day's weather, drawn as today's is at the top of the page (owner, 2026-10-16: "style it like today
   * is"): the day's sky, its high and the forecast's words, and under them the brief's line for the day ("Low 37°
   * Saturday night · Rain 0.4″"), the whole row opening the forecast on Weather. All three are the brief's
   * (comingUp.days), the day's name first (E14); nothing for a day it does not give.
   *
   * @param date - The day, "YYYY-MM-DD".
   */
  const comingDayWeather = (date: string): React.ReactNode => {
    const coming = brief?.comingUp.days?.[date] ?? null;
    if (!coming) return null;
    return (
      <div className={CARD}>
        <Link to="/weather#forecast" className={`${CARD_ROW} items-center bg-muted py-2.5 pr-3.5 transition-colors hover:bg-muted/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring`}>
          <span className={CARD_SLOT}><SkyIcon sky={coming.skyIcon} className="h-8 w-8 text-muted-foreground" strokeWidth={1.5} aria-hidden="true" /></span>
          <span className="min-w-0 flex-1">
            {/* E14: the day this weather is about, said first by the server ("Thursday:", "Tomorrow:"). */}
            <span className="mb-0.5 block text-sm font-semibold">{coming.day}</span>
            <span className="flex flex-wrap items-baseline gap-x-2">
              {coming.high && <span className="text-2xl font-semibold leading-none">{coming.high}</span>}
              {coming.skyText && <span className="text-sm">{coming.skyText}</span>}
            </span>
            {coming.text && <span className="mt-1 block text-sm text-muted-foreground">{coming.text}</span>}
          </span>
          <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
        </Link>
      </div>
    );
  };

  // Whether Today shows only the doors now (lib/todayStart); once the garden's place is known, so they never flash before the postcode step.
  const doorsOnly = showDoorsOnly({ noZip, placeKnown: gardenPostcode.known, started: gardenPostcode.started });

  /*
   * A garden with no plants (B30, rev 2): just "Your garden starts here" and the two doors, at once (B96). Only the garden
   * place is waited for (its `started`, the server's word): not the brief or the weather, so no
   * frost line, no "This week" line and no "No plants yet" line comes first.
   */
  /** B78: the sort-photos job the server names for a garden that has not started, when photos wait in the tray. */
  const startJob = todo?.startJob ? todo.jobs.find((job) => job.key === todo.startJob) ?? null : null;
  if (doorsOnly) {
    return (
      <div key="doors" className={`mx-auto -mt-2 w-full max-w-xl space-y-4 px-4 pb-4 pt-0 ${fadeIn}`}>
        {/* B78: photos waiting to be sorted, the server's one start job, above the two doors (B96). */}
        {startJob && todo && (
          <JobCard
            job={startJob}
            words={todo.words}
            busy={todoMarks.busyKey === startJob.key}
            failedWords={todoMarks.failed?.key === startJob.key ? todoMarks.failed.words : null}
            pending={todoMarks.pending[startJob.key]}
            onMark={(mark) => { void todoMarks.mark(startJob, mark); }}
          />
        )}
        <WelcomePlace onPhotos={onPhotos} onAdd={onAdd} />
      </div>
    );
  }

  // Not decided yet: a quiet loader rather than Today flashing up before the welcome.
  if (settling) {
    return (
      <GrowLoader variant="screen" steps={todaySteps} className="min-h-[50vh]" />
    );
  }

  // Pulled up into the layout's top padding (-mt-2): half the gap under the header (owner, 2026-10-09).
  return (
    <div key={step} className={`mx-auto -mt-2 w-full max-w-xl space-y-4 px-4 pb-4 pt-0 ${fadeIn}`}>
      {/* The very top: an urgent Notice rule (the frost or freeze warning is the header's bar, AlertBar). */}
      {!noZip && urgentNotice && <TodayNotice notice={urgentNotice} />}

      {/* Without a postal code, or if the day cannot be had, there is no season to paint. */}
      {!dayFailed && (
        <TodayHeader day={day} />
      )}

      {/* A founding gardener's welcome line, above the first step of the welcome. */}
      {noZip && <TopTip />}

      <div>
        {/* The page's name, as Plants, Beds and Seasons carry theirs. */}
        <h1 className="mb-1 text-2xl font-semibold text-foreground font-body">
          {t('nav.today')}
        </h1>
        {/* The Almanac's heading, the brief's words: "Fall almanac · Raleigh, NC", over the date; it wraps, never clipped. */}
        {heading && (
          <p className="text-xs font-semibold uppercase tracking-wider text-foreground">{heading}</p>
        )}
        {welcome ? (
          <>
            <h2 className="text-2xl font-bold leading-tight font-display" style={{ textWrap: 'balance' }}>{welcome}</h2>
            <p className="text-sm text-muted-foreground">{dateLine}</p>
          </>
        ) : (
          <>
            <h2 className="text-xl font-semibold leading-tight font-body">{dateLine}</h2>
            {/* B91: the Almanac under the date (the moon, the season, the day's other lines), with a postcode only. */}
            {!noZip && brief && <TodayAlmanac almanac={brief.almanac} rainShown={rainShown} />}
          </>
        )}
      </div>

      {/* The welcome's place step: the frost countdown and the week, then the ways to start. */}
      {!noZip && firstTip ? (
        <>
          {/* One reminder, when the server placed one; during the welcome it places
              only a beta tester's line or a founding gardener's welcome line,
              which matter most on the first visit. */}
          {notice ? <TodayNotice notice={notice} /> : <TopTip />}
          <WelcomePlace frost={countdown?.text ?? null} week={brief?.weekLine ?? null} onPhotos={onPhotos} onAdd={onAdd} />
        </>
      ) : noZip ? (
        <section className={PANEL} aria-labelledby="today-todo">
          <h2 id="today-todo" className={PANEL_TITLE}>{t('today.firstStep')}</h2>
          <TodayPostcode body={postcodeWords?.body} button={postcodeWords?.button} problem={gardenPostcode.problem} canGive={gardenPostcode.own} />
        </section>
      ) : (
        <>
          {/* "Water today?" (B152): the first thing under the Almanac, above the weather card; the water job under it when the day has one, else the one line. */}
          {waterAtTop && todo && water && <WaterSection water={water} todo={todo} marks={todoMarks} openKey={linkedJob} />}
          {/*
            The weather card (owner, 2026-10-09): the weather now on top, opening the Weather page; the frost
            countdown and the Almanac under it; while the list is on its way, or cannot be had, the card says so.
          */}
          <section className={CARD} aria-label={t('weather.title')}>
            {/* A garden the weather service cannot place yet (B44): the server's sentence where the weather would be. */}
            {!weatherNow && noForecastMessage && (
              <p className={`${CARD_TEXT_EDGE} py-2.5 text-sm text-muted-foreground`}>{noForecastMessage}</p>
            )}
            {(temperature || words || stripLine) && (
              <Link to="/weather" className={`${CARD_ROW} items-center bg-muted py-2.5 pr-3.5 transition-colors hover:bg-muted/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring`}>
                {/* The sky's key, the server's, in the app's standard icons (SkyIcon), not the forecast's own sketch, in the icon slot. */}
                <span className={CARD_SLOT}>
                  {weatherNow && <SkyIcon sky={weatherNow.icon} className="h-8 w-8 text-muted-foreground" strokeWidth={1.5} aria-hidden="true" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-baseline gap-x-2">
                    {temperature && <span className="text-2xl font-semibold leading-none">{temperature}</span>}
                    {words && <span className="text-sm">{words}</span>}
                  </span>
                  {stripLine && <span className="mt-1 block text-sm text-muted-foreground">{stripLine}</span>}
                </span>
                <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
              </Link>
            )}
            {/*
              The frost countdown, in the frost line's tint, leading to that season's dates on Weather. The Almanac's
              lines left the card for under the date (B91, option B, owner 2026-10-26).
            */}
            {countdown && (
              <div className={`${CARD_ROW} items-start py-1.5 pr-3.5 text-sm leading-snug ${TOKEN_TINT.cold}`}>
                <span className={`${CARD_SLOT} pt-0.5`} aria-hidden="true"><Snowflake className="h-4 w-4" /></span>
                <div className="min-w-0 flex-1">
                  <p>
                    <Link to={countdown.link} className={LINE_LINK}>
                      {/* OWNER-SET WORDING (2026-10-09), from the backend's dictionary (services/today/todayWords
                          countdown.about / open / openPast): "First frost most likely around Nov 1 · as early as
                          Oct 19" and the rest; never reworded or shortened without asking the owner. */}
                      {countdown.text}
                    </Link>
                  </p>
                </div>
              </div>
            )}
            {!todo && (noPlants ? (
              <p className={`${CARD_TEXT_EDGE} pb-3 pt-1 text-sm text-muted-foreground`}>{t('weather.noPlants')}</p>
            ) : waiting ? (
              // The garden and its weather are already showing above: only the last step is left to say (owner,
              // 2026-10-16: "the same three statements show when the today page loads and then for the weather box too").
              <GrowLoader variant="block" steps={[t('loader.today.jobs')]} className={`${CARD_TEXT_EDGE} pb-3 pt-1`} />
            ) : (
              // A failure on our side says the standard sentence; any other (no outlook in the brief), weather.error.
              <div className={`${CARD_TEXT_EDGE} pb-3 pt-1`}>
                {briefFailed && isServerFailure(briefError)
                  ? <ServerFailureNote error={briefError} where="Today brief" className="text-muted-foreground" />
                  : <p className="text-sm text-muted-foreground">{t('weather.error')}</p>}
              </div>
            ))}
            {/* B64, B137: the server's one-line note (every plant archived, or only kept seeds), the whole line a link to the path it sends. */}
            {brief?.note && (
              <p className={`${CARD_TEXT_EDGE} pb-3 pt-1 text-sm text-muted-foreground`}>
                <Link to={brief.note.link} className={LINE_LINK}>{brief.note.text}</Link>
              </p>
            )}
          </section>

          {/*
            The one to-do list (D51): From yesterday, Today, Coming up and When you can, the same cards as the top
            of Plants. "Water today?" is the list's first section, under the weather card, when the server places it there (its answer is not a job);
            the one reminder sits between Today and Coming up.
          */}
          {/* "Updating your garden..." while a fresh brief is on its way; the old jobs below look quieter until it arrives. */}
          <GuidanceUpdating where="Today" />
          {todo ? (
            <GuidanceQuiet>
            <TodoGroups
              todo={todo}
              marks={todoMarks}
              openKey={linkedJob}
              quietWhy={brief?.quiet?.text ?? null}
              dayWeather={comingDayWeather}
              onAdd={() => ownSheet.openAdd({ about: 'garden', id: null, name: '' })}
              onPlant={() => ownSheet.openPlant({ locationId: null })}
              between={notice ? <TodayNotice notice={notice} /> : <TopTip />}
              onEditOwn={(job) => {
                if (job.own) ownSheet.openEdit({ id: job.own.id, key: job.key, title: job.title, about: job.own.about, job: job.own.job, planned: job.own.planned, since: job.when.code === 'since' ? job.when.text : null });
              }}
              shownKey={ownSheet.openKey}
            />
            {ownSheet.element}
            </GuidanceQuiet>
          ) : (
            /* One reminder: the notice, else the advice under the weather, under the card. */
            notice ? <TodayNotice notice={notice} /> : <TopTip />
          )}
        </>
      )}

      {day && brief?.planner && !firstTip && (
        <section className={PANEL} aria-labelledby="today-week">
          <h2 id="today-week" className={PANEL_TITLE}>
            {t('today.thisWeek')}
            {/* Where these words come from: the printed planner's week, not the forecast. */}
            <span className="ml-auto font-normal normal-case tracking-normal">
              {t('today.fromPlanner')}
            </span>
          </h2>
          <div className="divide-y">
            {day.planner.map((note) => (
              // On a Spanish page the note's Spanish, where it is written; else the English.
              <p key={note.id} className="py-2 text-sm leading-relaxed first:pt-0 last:pb-0">{(language.startsWith('es') && note.textEs) || note.text}</p>
            ))}
          </div>
        </section>
      )}

      {/* Every gardener: what is confusing or broken, said where they are (Report a problem; B67). */}
      {!noZip && !firstTip && canReportProblem() && (
        <p className="pt-2 text-center text-sm text-muted-foreground">
          {t('today.reportPrompt')}{' '}
          <button
            type="button"
            onClick={() => { openReportProblem(); }}
            className="font-semibold text-primary underline-offset-4 hover:underline"
          >
            {t('today.report')}
          </button>
        </p>
      )}
    </div>
  );
};

/**
 * The page at /today: the welcome's steps, or the day with its jobs.
 *
 * It owns the photo picker of the photo door (usePhotoAdd, the shared photo path, B80) and the add form of the type door and
 * draws their sheets beside the views, never inside one: filing the first plants turns the garden from "no plants"
 * into "plants", which swaps the views, and a sheet and its flow must not unmount or lose state at that moment
 * (B30, design revision 3).
 */
export const TodayPage: React.FC = () => {
  // The photo picker of the photo door: the photos go to the tray, where the server's guess says what each is (B30 rev 4; B80).
  const photos = useDoorPhotos();
  // The type door's add form: the Plants page's own form and save, over Today, outside the swap (B30 rev 5).
  const addForm = useDoorAddForm();
  return (
    <>
      <TodayView onPhotos={photos.open} onAdd={addForm.open} />
      {photos.element}
      {addForm.element}
    </>
  );
};
